import { apiGet, apiPost, apiDelete } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// PhraseList API — STT phrase-biasing buckets/terms and agent attachments.
// Mirrors agentKnowledge.ts. The Tenant controllers return RAW lists (not paged),
// so the envelope is OperationResult<T[]> and `data` is the array directly.
// View shapes are camelCased by the client interceptor from the C# DTOs in
// OO.Domain/AIInfo/PhraseListViews.cs.
// ─────────────────────────────────────────────────────────────────────────────

/** A phrase bucket in the library / attach list. */
export interface PhraseGroupView {
  id: number;
  title: string;
  description?: string | null;
  merchantId?: number | null;
  isActive: boolean;
  termCount: number;
}

/** A bucket attached to an agent. `linkId` is the join-row id used to detach. */
export interface AgentPhraseGroupView {
  linkId: number;
  groupId: number;
  title: string;
  description?: string | null;
  merchantId?: number | null;
  linkActive: boolean;
  termCount: number;
}

/** A term within a bucket. `itemId` is the membership-row id used to remove it. */
export interface PhraseTermView {
  itemId: number;
  termId: number;
  term: string;
  description?: string | null;
  languageCode?: string | null;
  isActive: boolean;
  sortOrder: number;
}

// ── Agent ↔ bucket ───────────────────────────────────────────────────────────

export async function getPhraseGroupsForAgent(agentId: number): Promise<AgentPhraseGroupView[]> {
  const result = await apiGet<OperationResult<AgentPhraseGroupView[]>>(
    '/api/AiAgentPhraseTermGroup/Search',
    { aiAgentId: agentId },
  );
  if (!result.isSuccess) throw new Error(result.message || 'Failed to load phrase buckets for agent');
  return result.data ?? [];
}

export async function attachPhraseGroupToAgent(agentId: number, phraseTermGroupId: number): Promise<void> {
  const result = await apiPost<OperationResult<unknown>>('/api/AiAgentPhraseTermGroup/Add', {
    aiAgentId: agentId,
    phraseTermGroupId,
  });
  if (!result.isSuccess) throw new Error(result.message || 'Failed to attach phrase bucket to agent');
}

export async function detachPhraseGroupFromAgent(linkId: number): Promise<void> {
  const result = await apiDelete<OperationResult<unknown>>(`/api/AiAgentPhraseTermGroup/Remove/${linkId}`);
  if (!result.isSuccess) throw new Error(result.message || 'Failed to detach phrase bucket from agent');
}

// ── Bucket library ───────────────────────────────────────────────────────────

export async function getAvailablePhraseGroups(): Promise<PhraseGroupView[]> {
  const result = await apiGet<OperationResult<PhraseGroupView[]>>('/api/PhraseTermGroup/Search');
  if (!result.isSuccess) throw new Error(result.message || 'Failed to load phrase buckets');
  return result.data ?? [];
}

export async function getTermsForGroup(groupId: number): Promise<PhraseTermView[]> {
  const result = await apiGet<OperationResult<PhraseTermView[]>>(`/api/PhraseTermGroup/Terms/${groupId}`);
  if (!result.isSuccess) throw new Error(result.message || 'Failed to load terms');
  return result.data ?? [];
}

export async function createPhraseGroup(title: string, description?: string): Promise<PhraseGroupView> {
  const result = await apiPost<OperationResult<PhraseGroupView>>('/api/PhraseTermGroup/Add', { title, description });
  if (!result.isSuccess || !result.data) throw new Error(result.message || 'Failed to create bucket');
  return result.data;
}

export async function updatePhraseGroup(
  id: number,
  title: string,
  description: string | undefined,
  isActive: boolean,
): Promise<void> {
  const result = await apiPost<OperationResult<unknown>>('/api/PhraseTermGroup/Update', {
    id,
    title,
    description,
    isActive,
  });
  if (!result.isSuccess) throw new Error(result.message || 'Failed to update bucket');
}

// ── Terms in a bucket ────────────────────────────────────────────────────────

export async function addTermToGroup(
  phraseTermGroupId: number,
  term: string,
  description?: string,
  languageCode?: string,
): Promise<void> {
  const result = await apiPost<OperationResult<unknown>>('/api/PhraseTerm/Add', {
    phraseTermGroupId,
    term,
    description,
    languageCode,
  });
  if (!result.isSuccess) throw new Error(result.message || 'Failed to add term');
}

export async function updateTerm(
  termId: number,
  term: string,
  description: string | undefined,
  languageCode: string | undefined,
  isActive: boolean,
): Promise<void> {
  const result = await apiPost<OperationResult<unknown>>('/api/PhraseTerm/Update', {
    termId,
    term,
    description,
    languageCode,
    isActive,
  });
  if (!result.isSuccess) throw new Error(result.message || 'Failed to update term');
}

export async function removeTermFromGroup(groupId: number, termId: number): Promise<void> {
  const result = await apiDelete<OperationResult<unknown>>(`/api/PhraseTerm/Remove/${groupId}/${termId}`);
  if (!result.isSuccess) throw new Error(result.message || 'Failed to remove term');
}
